import { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import ReactPlayer from 'react-player'
import screenfull from 'screenfull'

import OnVideoToggleFullScreen from '../../../ui/hooks/onVideoToggleFullScreen'
import { SWIPER_NO_SWIPING_CLASS } from '../../../../helpers/const'
import VideoBar from './VideoBar'

const StyledVideoWrapper = styled.div({
  position: 'relative',
  width: '100%',
  '& > div:first-of-type': {
    width: '100% !important',
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const VideoPlayer = ({
  theme,
  src,
  isAutoPlayEnabled,
  onPlayerProgress,
  onVideoEnterFullscreen,
  onVideoStart,
  onVideoError,
  videoRef,
}) => {
  const [playing, setPlaying] = useState(!!isAutoPlayEnabled)
  const [muted, setMuted] = useState(!!isAutoPlayEnabled)
  const [played, setPlayed] = useState(0)
  const [seeking, setSeeking] = useState(false)
  const nativeControlsNeeded = !screenfull.isEnabled

  const onFullScreenChange = isFullScreen => {
    if (isFullScreen) onVideoEnterFullscreen()
  }

  OnVideoToggleFullScreen(
    videoRef?.current,
    {
      callback: isFullScreen => onFullScreenChange(isFullScreen),
    },
  )

  const onSeeking = () => setSeeking(true)

  const onProgress = value => {
    setPlayed(value.played)
    onPlayerProgress(videoRef, seeking)
  }

  const onSeekChange = e => {
    setSeeking(true)
    setPlayed(e.target.value)
    videoRef.current.seekTo(parseFloat(e.target.value))
  }

  const togglePlaying = () => setPlaying(!playing)
  const toggleFullscreen = () => {
    const { current: { wrapper } } = videoRef
    const videoDOM = wrapper.children[0]
    screenfull.toggle(videoDOM)
  }
  const toggleMuted = () => setMuted(!muted)

  const { mediaSizes: { desktop, mobile } } = theme

  return (
    <StyledVideoWrapper>
      <ReactPlayer
        className={nativeControlsNeeded ? SWIPER_NO_SWIPING_CLASS : ''}
        playsinline
        onStart={() => onVideoStart()}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        playing={playing}
        onProgress={onProgress}
        onSeeking={onSeeking}
        onError={onVideoError}
        ref={videoRef}
        height={window.innerWidth < 768 ? mobile.height : desktop.height}
        url={src}
        muted={muted}
        controls={nativeControlsNeeded}
      />
      {
        !nativeControlsNeeded && (
          <VideoBar
            muted={muted}
            playing={playing}
            played={played}
            onChange={onSeekChange}
            togglePlaying={togglePlaying}
            toggleFullscreen={toggleFullscreen}
            toggleMuted={toggleMuted}
          />
        )
      }
    </StyledVideoWrapper>
  )
}

VideoPlayer.propTypes = {
  theme: PropTypes.shape({
    mediaSizes: PropTypes.shape({
      desktop: PropTypes.shape({
        height: PropTypes.string,
      }),
      mobile: PropTypes.shape({
        height: PropTypes.string,
      }),
    }),
  }).isRequired,
  src: PropTypes.string,
  isAutoPlayEnabled: PropTypes.bool,
  onPlayerProgress: PropTypes.func.isRequired,
  onVideoEnterFullscreen: PropTypes.func.isRequired,
  videoRef: PropTypes.shape({
    current: PropTypes.shape({
      seekTo: PropTypes.func.isRequired,
      wrapper: PropTypes.shape({
        children: PropTypes.shape({}).isRequired,
      }).isRequired,
    }),
  }).isRequired,
  onVideoStart: PropTypes.func,
  onVideoError: PropTypes.func,
}

VideoPlayer.defaultProps = {
  src: '',
  isAutoPlayEnabled: false,
  onVideoStart: () => false,
  onVideoError: () => false,
}

VideoPlayer.displayName = 'VideoPlayer'
VideoPlayer.whyDidYouRender = true

export default VideoPlayer
